<template>
    <div>
        <b-row>
            <b-col>
                <StripeVueCard :patient="patient" @cardAdded="newCardAdded" />
            </b-col>
        </b-row>
        <b-row v-if="filteredCards.length">
            <b-col>
                <b-table striped :items="filteredCards" :fields="fields" class="table-shopping border">
                    <template #cell(brand)="{ item }">
                        <span class="d-inline-flex align-items-center" style="gap: 6px; text-transform: capitalize;">
                            <i :class="brandIcon(item.brand)" class="fa-lg fa-fw" aria-hidden="true"></i>
                            {{ item.brand }}
                        </span>
                    </template>
                    <template #cell(expiration)="{ item }">
                        {{ item.expMonth }}/{{ item.expYear }}
                    </template>
                    <template #cell(hasActiveSubscription)="{ item }">
                        {{ item.hasActiveSubscription ? "✔" : "" }}
                    </template>
                    <template #cell(actions)="{ item }">
                        <div class="text-right">
                            <a href="#" @click.prevent="deleteConfirmDialog(item)">
                                <b-icon icon="x-circle-fill" class="mr-1" variant="danger" v-b-tooltip.hover
                                    title="Remove" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import axios, { AxiosRequestConfig } from 'axios';
import StripeVueCard from '@/components/Payments/StripeVueCardcomponent.vue';
import { StripeCard } from '@/models/Payment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { cardBrandIcon } from '@/util/stripeCardBrand';

@Component({
    name: "PatientPaymentMethodsModal",
    components: {
        StripeVueCard,
    }
})

export default class PatientPaymentMethodsModal extends Vue {
    @Prop() patient!: Patient;

    private stripeCustomerId: string | null = this.patient?.stripeCustomerId;
    private stripeCardList: Array<StripeCard> = [];

    created() {
        this.refresh();
    }

    @Watch('patient.stripeCustomerId')
    refresh() {
        this.stripeCustomerId = this.patient?.stripeCustomerId;
        this.fetchPatientPaymentMethods();
    }

    newCardAdded(stripeResponse: StripeCard) {
        this.stripeCustomerId = stripeResponse.customerId;
        this.fetchPatientPaymentMethods();
    }

    async fetchPatientPaymentMethods(): Promise<number | any> {
        try {
            this.stripeCardList = [];
            if (!this.stripeCustomerId) return;
            const response = await axios.get(`/stripe/cards/${this.stripeCustomerId}`);
            this.stripeCardList = response.data;
        } catch (error) {
            console.error("Error while getting Payment Cards from Patient", error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async deleteCard(card: StripeCard): Promise<number | any> {
        try {
            const config: AxiosRequestConfig = {
                params: {
                    patientid: this.patient.id,
                    customerid: card.customerId
                }
            };
            await axios.delete(`/stripe/${card.id}`, config);
        } catch (error) {
            console.error("Error deleting Payment Card from Patient", error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.fetchPatientPaymentMethods();
        }
    }

    async deleteConfirmDialog(card: StripeCard) {
        try {
            const msg = "Are you sure you want to delete this Payment Method?";
            const result = await this.$bvModal.msgBoxConfirm(msg, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!result) return;
            await this.deleteCard(card);
        } catch (err) {
            console.error("Error caught on Delete button.", err);
        }
    }

    get filteredCards() {
        return this.stripeCardList?.filter(p => !p.pendingDelete);
    }

    get fields() {
        const fields: any[] = [
            { key: "brand", label: "Card" },
            { key: "cardNumber", label: "Number" },
            { key: "expiration", label: "Expire" },
        ];

        if (this.filteredCards.some(fc => fc.hasActiveSubscription)) {
            fields.push({ key: "hasActiveSubscription", label: "On Active Subscription" });
        }

        fields.push({ key: "actions", label: "", thClass: "text-right" });
        return fields;
    }

    brandIcon(brand: string): string {
        return cardBrandIcon(brand);
    }
}
</script>
